import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, CalendarDays, Cake, Clock3, FileClock, Users, UserCheck, UserX,
  Building2, Layers, LogIn, LogOut as LogOutIcon, ArrowRight,
} from "lucide-react";
import { staffApi, fmtDate, fmtTime } from "../api";
import { useAccess } from "../permissions";

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-white border border-slate-200 p-6" data-testid={testid}>
    <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
      <Icon size={14} className="text-[#C5221F]" /> {title}
    </p>
    <div className="mt-4">{children}</div>
  </div>
);

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-slate-200 p-5" data-testid={testid}>
    <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
      <Icon size={13} className="text-[#C5221F]" /> {label}
    </p>
    <p className="mt-2 font-display text-3xl font-extrabold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
  </div>
);

const QUICK_LINKS = [
  { label: "Employee Master", to: "/app/hrms/employees", testid: "dash-quick-employees" },
  { label: "Attendance", to: "/app/attendance/all", testid: "dash-quick-attendance" },
  { label: "Leave Approvals", to: "/app/hrms/leaves", testid: "dash-quick-leaves" },
  { label: "Reports", to: "/app/reports", testid: "dash-quick-reports" },
];

// Privileged dashboard — real production figures only; any stat whose source failed to load is hidden.
const WorkforceDashboard = () => {
  const [live, setLive] = useState(null);
  const [users, setUsers] = useState(null);
  const [leaves, setLeaves] = useState(null);
  const [branches, setBranches] = useState(null);
  const [departments, setDepartments] = useState(null);

  useEffect(() => {
    const load = async () => {
      const [lv, u, l, b, d] = await Promise.allSettled([
        staffApi.get("/monitor/live"),
        staffApi.get("/users"),
        staffApi.get("/leaves"),
        staffApi.get("/branches"),
        staffApi.get("/departments"),
      ]);
      if (lv.status === "fulfilled") setLive(lv.value.data);
      if (u.status === "fulfilled") setUsers(Array.isArray(u.value.data) ? u.value.data : []);
      if (l.status === "fulfilled") setLeaves(Array.isArray(l.value.data) ? l.value.data : []);
      if (b.status === "fulfilled") setBranches(Array.isArray(b.value.data) ? b.value.data : []);
      if (d.status === "fulfilled") setDepartments(Array.isArray(d.value.data) ? d.value.data : []);
    };
    load();
  }, []);

  const stats = [
    live && { icon: Users, label: "Total Employees", value: live.total_employees, testid: "stat-total" },
    users && { icon: UserCheck, label: "Active Employees", value: users.filter((u) => u.active !== false).length, testid: "stat-active" },
    live && { icon: LogIn, label: "Present Today", value: live.checked_in_today, testid: "stat-present" },
    live && { icon: UserX, label: "Absent Today", value: live.absent_today, testid: "stat-absent" },
    live && { icon: Clock3, label: "Late Today", value: live.late_today, testid: "stat-late" },
    leaves && { icon: FileClock, label: "Pending Leave Requests", value: leaves.filter((l) => l.status === "Pending").length, testid: "stat-pending-leaves" },
    branches && { icon: Building2, label: "Branches", value: branches.length, testid: "stat-branches" },
    departments && { icon: Layers, label: "Departments", value: departments.length, testid: "stat-departments" },
  ].filter(Boolean);

  const feed = Array.isArray(live?.activity_feed) ? live.activity_feed : [];

  return (
    <div data-testid="staff-dashboard">
      <h1 className="font-display text-2xl font-bold text-slate-900">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">
        Workforce overview{live?.generated_at ? ` · live data updated ${fmtTime(live.generated_at)}` : ""}.
      </p>

      {stats.length > 0 && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dash-stats-grid">
          {stats.map((s) => <Stat key={s.testid} {...s} />)}
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dash-quick-links">
        {QUICK_LINKS.map((ql) => (
          <Link key={ql.to} to={ql.to} data-testid={ql.testid}
            className="group bg-[#0A1128] hover:bg-[#16204a] text-white p-5 flex items-center justify-between transition-colors">
            <span className="font-display text-xs font-semibold uppercase tracking-widest">{ql.label}</span>
            <ArrowRight size={14} className="text-[#E8574F] group-hover:translate-x-1 transition-transform" />
          </Link>
        ))}
      </div>

      {feed.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="dash-activity-feed">
          <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Recent Activity</p>
          <ul className="mt-4 divide-y divide-slate-100">
            {feed.slice(0, 10).map((f, i) => (
              <li key={i} className="py-2.5 text-sm text-slate-700 flex items-center gap-2">
                {(f.type || "").toLowerCase().includes("out") ? <LogOutIcon size={13} className="text-slate-400 shrink-0" /> : <LogIn size={13} className="text-emerald-600 shrink-0" />}
                <span className="font-medium">{f.user_name || f.name || "Employee"}</span>
                <span className="text-xs text-slate-400">{f.type || f.event_type || ""} {f.time || f.timestamp ? `· ${fmtTime(f.time || f.timestamp)}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Personal dashboard — the pre-GR-CONNECT employee experience, unchanged.
const PersonalDashboard = () => {
  const [today, setToday] = useState(null);
  const [recent, setRecent] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [errors, setErrors] = useState(0);

  useEffect(() => {
    const load = async () => {
      const [t, m, h, b, l] = await Promise.allSettled([
        staffApi.get("/attendance/today"),
        staffApi.get("/attendance/mine", { params: { limit: 5 } }),
        staffApi.get("/holidays"),
        staffApi.get("/birthdays/today"),
        staffApi.get("/leaves/mine"),
      ]);
      setErrors([t, m, h, b, l].filter((r) => r.status === "rejected").length);
      if (t.status === "fulfilled") setToday(t.value.data);
      if (m.status === "fulfilled") setRecent(Array.isArray(m.value.data) ? m.value.data : []);
      if (h.status === "fulfilled") setHolidays(Array.isArray(h.value.data) ? h.value.data : []);
      if (b.status === "fulfilled") setBirthdays(Array.isArray(b.value.data) ? b.value.data : []);
      if (l.status === "fulfilled") setLeaves(Array.isArray(l.value.data) ? l.value.data : []);
    };
    load();
  }, []);

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const upcoming = holidays.filter((h) => h.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date));
  const pending = leaves.filter((l) => l.status === "Pending");

  return (
    <div data-testid="staff-dashboard">
      <h1 className="font-display text-2xl font-bold text-slate-900">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Your attendance, leave and company updates at a glance.</p>
      {errors > 0 && (
        <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2.5 rounded-sm" data-testid="dashboard-partial-error">
          Some information could not be loaded. Please refresh in a moment.
        </p>
      )}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Card icon={Clock3} title="Today" testid="dash-today-card">
          {today ? (
            <>
              <p className="font-display text-lg font-bold text-slate-900" data-testid="dash-today-status">{today.status || "—"}</p>
              <p className="mt-1 text-xs text-slate-500">In: {fmtTime(today.check_in_time)} · Out: {fmtTime(today.check_out_time)}</p>
              {today.branch_name && <p className="mt-1 text-xs text-slate-400">{today.branch_name}</p>}
            </>
          ) : (
            <p className="text-sm text-slate-500" data-testid="dash-today-empty">No attendance recorded today yet. Check-in is available in the mobile app.</p>
          )}
        </Card>
        <Card icon={FileClock} title="Pending Leave Requests" testid="dash-leaves-card">
          <p className="font-display text-3xl font-extrabold text-slate-900" data-testid="dash-pending-count">{pending.length}</p>
          <Link to="/app/leave" className="mt-2 inline-block text-xs font-semibold text-[#C5221F] hover:underline" data-testid="dash-goto-leave">View leave →</Link>
        </Card>
        <Card icon={CalendarDays} title="Next Holiday" testid="dash-holiday-card">
          {upcoming.length ? (
            <>
              <p className="font-display text-lg font-bold text-slate-900" data-testid="dash-next-holiday">{upcoming[0].name}</p>
              <p className="mt-1 text-xs text-slate-500">{fmtDate(upcoming[0].date)}</p>
            </>
          ) : (
            <p className="text-sm text-slate-500">No upcoming holidays listed.</p>
          )}
        </Card>
      </div>
      <div className="mt-5 grid lg:grid-cols-2 gap-5">
        <Card icon={CalendarCheck} title="Recent Attendance" testid="dash-recent-card">
          {recent.length ? (
            <ul className="divide-y divide-slate-100" data-testid="dash-recent-list">
              {recent.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-700 font-medium">{fmtDate(r.date)}</span>
                  <span className="text-xs text-slate-500">{fmtTime(r.check_in_time)} – {fmtTime(r.check_out_time)}</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{r.status || "—"}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No recent attendance records.</p>
          )}
          <Link to="/app/attendance" className="mt-3 inline-block text-xs font-semibold text-[#C5221F] hover:underline" data-testid="dash-goto-attendance">Full history →</Link>
        </Card>
        <Card icon={Cake} title="Birthdays Today" testid="dash-birthdays-card">
          {birthdays.length ? (
            <ul className="space-y-2" data-testid="dash-birthdays-list">
              {birthdays.map((b) => (
                <li key={b.id} className="text-sm text-slate-700">{b.name}{b.department ? <span className="text-xs text-slate-400"> · {b.department}</span> : null}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No birthdays today.</p>
          )}
        </Card>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const access = useAccess();
  return access.adminArea ? <WorkforceDashboard /> : <PersonalDashboard />;
};

export default Dashboard;
